import classNames from "classnames";
import { useQueryString } from "hooks/general-hooks";
import { buildQueryParams, getDeadlockImagePath, removeKeys } from "lib/general-helper";
import { Link, useLocation, LinkProps } from "react-router-dom";
import { Abilities } from "./BuildPageContainer";

export interface TabsData {
  tabName: string;
  isRecommended: boolean;
  buildIndex: number;
  firstAbility: string;
}

interface BuildTabsContainerProps {
  tabsData: TabsData[];
  onClick: (buildIndex: number) => void;
  abilitiesBySlot: Abilities;
}

const BuildTabsContainer = (props: BuildTabsContainerProps) => {
  const { tabsData, onClick, abilitiesBySlot } = props;

  const urlParams: any = useQueryString();
  const location = useLocation();

  return (
    <div className={classNames("flex gap-[12px]")}>
      {tabsData.map((data, idx) => {
        let queryParamsObj = { ...urlParams, ...{ multi: data.tabName + "_" + data.buildIndex } };
        if (data.isRecommended) {
          queryParamsObj = removeKeys(queryParamsObj, ["multi"]);
        }
        const newQueryParams = buildQueryParams(queryParamsObj);

        const key = Object.keys(abilitiesBySlot).find((key) => abilitiesBySlot[key].name === data.firstAbility);
        const firstAbilityImgPath = abilitiesBySlot[key ?? ""]?.abilityImage;
        const firstAbilityImg = getDeadlockImagePath(firstAbilityImgPath, data.firstAbility);

        {
          return data.tabName ? (
            <BuildTab
              label={data.tabName}
              isActive={urlParams?.multi === data.tabName + "_" + data.buildIndex || (data.isRecommended && !urlParams?.multi)}
              isDefault={data.isRecommended}
              key={idx}
              onClick={() => onClick?.(data.buildIndex)}
              linkProps={{ to: { pathname: location.pathname, search: newQueryParams } }}
              firstAbility={data.firstAbility}
              firstAbilityImg={firstAbilityImg}
            />
          ) : null;
        }
      })}
    </div>
  );
};

interface BuildTabProps {
  label?: string;
  isActive: boolean;
  isDefault?: boolean;
  linkProps: LinkProps;
  onClick?: () => void;
  firstAbility: string;
  firstAbilityImg: string;
}

function BuildTab(props: BuildTabProps) {
  const { label, isActive, isDefault, linkProps, onClick, firstAbility, firstAbilityImg } = props;

  const modifiedLabel = isDefault ? "Recommended: " + label : label;

  let buildTypeIcon = "https://static.bigbrain.gg/assets/deadlock/weapon_icon_multibuilds.png";
  let buildTypeAlt = "Weapon";
  if (label?.toLowerCase().includes("spirit")) {
    buildTypeIcon = "https://static.bigbrain.gg/assets/deadlock/spirit_icon_multibuilds.png";
    buildTypeAlt = "Spirit";
  } else if (label?.toLowerCase().includes("tank") || label?.toLowerCase().includes("support")) {
    buildTypeIcon = "https://static.bigbrain.gg/assets/deadlock/vitality_icon_multibuilds.png";
    buildTypeAlt = "Vitality";
  } else if (label?.toLowerCase().includes("bruiser")) {
    buildTypeIcon = "https://static.bigbrain.gg/assets/deadlock/bruiser_icon_multibuilds.png";
    buildTypeAlt = "Bruiser";
  } else if (label?.toLowerCase().includes("hybrid")) {
    buildTypeIcon = "https://static.bigbrain.gg/assets/deadlock/hybrid_icon_multibuilds.png";
    buildTypeAlt = "Hybrid";
  }

  return (
    <Link
      className={classNames("flex items-center px-[12px] py-[10px] h-[42px] rounded-t-[3px] hover:cursor-pointer", {
        "bg-purple-200": !isActive,
        "bg-[#1f1f41]": isActive,
        "shadow-[inset_0px_2px_0px_rgba(50,115,250,0.5),inset_0px_-2px_2px_rgba(0,0,0,0.2)]": !isDefault && !isActive,
        "shadow-[inset_0px_2px_0px_rgba(255,155,0,0.5),inset_0px_-2px_4px_rgba(0,0,0,0.2)]": isDefault && !isActive,
        "shadow-[inset_0px_2px_0px_#3273fa]": !isDefault && isActive,
        "shadow-[inset_0px_2px_0px_#ff9b00]": isDefault && isActive,
      })}
      {...linkProps}
      onClick={() => onClick?.()}
    >
      {modifiedLabel && (
        <div className="relative font-['Inter'] text-[14px] leading-[20px] tracking-[0px] text-left whitespace-nowrap">
          <span className={classNames("text-lavender-300 font-medium", { "opacity-0": isActive })}>{modifiedLabel}</span>
          <strong className={classNames("absolute left-0 text-white font-bold", { "opacity-0": !isActive })}>
            {modifiedLabel}
          </strong>
        </div>
      )}
      <div
        className={classNames("flex items-center justify-center ml-[12px] w-[20px] h-[20px] opacity-50", {
          "!opacity-100": isActive,
        })}
      >
        <img src={buildTypeIcon} alt={buildTypeAlt} />
      </div>
      <div
        className={classNames("flex items-center justify-center ml-[6px] w-[20px] h-[20px] opacity-50", {
          "!opacity-100": isActive,
        })}
      >
        <img
          className="w-[20px] h-[20px]"
          src={`https://static.bigbrain.gg/deadlock/staticdata/panorama/images${firstAbilityImg}.png`}
          alt={firstAbility}
        />
      </div>
    </Link>
  );
}

export default BuildTabsContainer;
